import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";
import { MiksiToimii } from "@/components/hub/Callouts";

const SLUG = "montessori-myytit";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori-myytit — neljä yleistä väärinkäsitystä";
const DESC =
  "Vaatiiko Montessori kalliita puuleluja? Saako lapsi tehdä mitä haluaa? Neljä sitkeää myyttiä ja se, mistä Montessori-ajattelussa oikeasti on kyse.";
const PUBLISHED = "2026-08-16";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/montessori-myytit")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Montessori-myytit"
      title={TITLE}
      lead="Montessorista puhutaan paljon, ja osa puheesta koskee enemmän sisustusta kuin kasvatusta. Tässä neljä yleistä väärinkäsitystä ja se, mitä Montessori-ajattelu käytännössä tarkoittaa kotona."
      readingMinutes={6}
      publishedISO={PUBLISHED}
      updatedISO={PUBLISHED}
    >
      <h2>Myytti 1: ”Montessori tarkoittaa kalliita puuleluja”</h2>
      <p>Montessori on ensisijaisesti kasvatusfilosofia ja tapa järjestää ympäristö, ei tuoteluokka. Keskeistä on, että lapsi löytää tavarat itse, ymmärtää mihin ne palautetaan ja pääsee osallistumaan arkeen omalla tasollaan.</p>
      <p>Käytännössä moni muutos tehdään kodin tavallisilla esineillä: korilla, pienellä kannulla, yhdellä koukulla oikealla korkeudella. Materiaali ei ratkaise — saavutettavuus, järjestys ja rajattu valikoima ratkaisevat.</p>

      <MiksiToimii>
        Kun tavaroita on esillä vähemmän ja jokaisella on oma paikkansa, valitseminen ja siivoaminen ovat lapselle mahdollisia ilman aikuisen apua. Tämä voi tukea itsenäistä toimintaa enemmän kuin yksittäisen lelun materiaali.
      </MiksiToimii>

      <h2>Myytti 2: ”Montessori-lapsi saa tehdä mitä haluaa”</h2>
      <p>Montessorissa puhutaan vapaudesta rajojen sisällä. Lapsi saa valita työnsä valmistellusta ympäristöstä ja jatkaa sitä niin kauan kuin keskittyminen kestää — mutta ympäristön, turvallisuuden ja yhteisten sopimusten rajat pysyvät aikuisen vastuulla.</p>
      <p>Aikuisen rooli ei siis katoa. Se siirtyy jatkuvasta ohjaamisesta ympäristön valmisteluun, havainnointiin ja johdonmukaisiin rajoihin.</p>

      <h2>Myytti 3: ”Montessori-kodin pitää olla beige”</h2>
      <p>Rauhallinen väripaletti on tyylivalinta, ei Montessorin vaatimus. Tärkeämpää on, että tila on järjestyksessä, tavarat ovat saavutettavissa ja jokaisella esineellä on tarkoitus.</p>
      <p>Värikkäät kirjat, lapsen omat piirustukset ja perheen tavarat kuuluvat kotiin. Estetiikka on toissijaista saavutettavuuteen, järjestykseen ja käyttötarkoitukseen nähden.</p>

      <h2>Myytti 4: ”Montessori kieltää mielikuvitusleikit”</h2>
      <p>Tämä on eniten yksinkertaistettu kohta. Montessori-pedagogiikassa painotetaan pienillä lapsilla todellisuuteen kiinnittyvää toimintaa: aitoja työkaluja, aitoja tehtäviä ja kokemuksia, joita lapsi voi itse tutkia. Ajatus on, että pieni lapsi vasta rakentaa käsitystään maailmasta.</p>
      <p>Se ei tarkoita, että roolileikki olisi kielletty tai haitallinen. Lapset leikkivät joka tapauksessa, ja Montessori-ympäristöissä leikitään. Ero on painotuksessa: valmiiden fantasiakehysten sijaan tarjotaan mahdollisuuksia osallistua oikeaan arkeen. Aihetta tulkitaan eri Montessori-suuntauksissa hieman eri tavoin.</p>

      <h2>Mitä Montessori sitten on?</h2>
      <p>Lyhyesti: ympäristö, joka on rakennettu lapsen mittakaavaan, aikuinen joka havainnoi enemmän kuin ohjaa, ja arki jossa lapsi saa harjoitella oikeita tehtäviä omaan tahtiinsa.</p>
      <p>
        Käytännön aloitus löytyy oppaasta{" "}
        <Link to="/montessori-koulu/montessori-kotona">Montessori kotona</Link>. Menetelmän taustaa avaa{" "}
        <Link to="/montessori-koulu/maria-montessori-elamakerta">Maria Montessorin elämäkerta</Link>.
      </p>
    </BlogPost>
  );
}